import uniq from 'lodash/uniq';

import { FormState } from '../StandardInputsTab/_types';
import isInteger from 'lodash/isInteger';

const normalizeTimeValue = (value: string) => {
  if (value.startsWith('0')) {
    return value.substring(1);
  }
  return value;
};

const getCronPartsFromHhMm = (hhMm: string) => {
  const [hh, mm] = hhMm.split(':');
  return [normalizeTimeValue(hh), normalizeTimeValue(mm)];
};

// A step of 0 serializes to `*/0`: cron-validator accepts it, but no scheduler
// can run it. Without this guard that value reaches the API as a broken cron
// string. Centralizing the rule here keeps callers from each re-deriving it.
const isValidRepeatEvery = (repeatEvery: number) => {
  return isInteger(repeatEvery) && repeatEvery >= 1;
};

export const formStateToCron = (formState: FormState): string | undefined => {
  switch (formState.type) {
    case 'Week': {
      // Without a weekday the cron ends in a trailing comma (`0 0 * * `),
      // which is malformed rather than merely empty.
      if (!formState.on.length) {
        return;
      }
      const [hours, minutes] = getCronPartsFromHhMm(formState.at);
      const weekdays = formState.on.map((on) =>
        on.substring(0, 3).toLocaleLowerCase()
      );
      return `${minutes} ${hours} * * ${weekdays.join(',')}`;
    }
    case 'Day': {
      if (!isValidRepeatEvery(formState.repeatEvery)) {
        return;
      }
      const [hours, minutes] = getCronPartsFromHhMm(formState.at);
      return `${minutes} ${hours} */${formState.repeatEvery} * *`;
    }
    case 'Hour': {
      if (!isValidRepeatEvery(formState.repeatEvery)) {
        return;
      }
      const minutes = normalizeTimeValue(formState.startAtMinute);
      return `${minutes} */${formState.repeatEvery} * * *`;
    }
    case 'Minute': {
      if (!isValidRepeatEvery(formState.repeatEvery)) {
        return;
      }
      const [hours, minutes] = getCronPartsFromHhMm(formState.startAt);
      return `${minutes === '0' ? '*' : minutes}/${
        formState.repeatEvery
      } ${hours}-23 * * *`;
    }
    case 'Month': {
      if (!isValidRepeatEvery(formState.repeatEvery)) {
        return;
      }
      const [hours, minutes] = getCronPartsFromHhMm(formState.at);
      const days = formState.onDay.filter((day) => {
        const numericDay = Number(day);
        return !(isNaN(numericDay) || numericDay < 1 || numericDay > 31);
      });
      if (!days.length) {
        return;
      }
      return `${minutes} ${hours} ${uniq(days).join(',')} ${
        formState.repeatEvery > 1 ? `*/${formState.repeatEvery}` : '*'
      } *`;
    }
  }
  return undefined;
};
